import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { approveDevice, fetchDeviceApprovals } from "../../api/devices.js";
import type { PendingDeviceApproval } from "../../api/devices.js";
import { describeAuthError } from "../../app/authFlow.js";
import { formatRelativeTime } from "../../lib/format.js";
import { TextInput } from "../../ui/forms.js";
import { Alert, Button, Section } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";

const APPROVALS_KEY: ReadonlyArray<string> = ["devices", "approvals"];

function ApprovalRow(props: { readonly approval: PendingDeviceApproval; readonly onApproved: () => void }): ReactNode {
  const toast = useToast();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const { approval } = props;

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await approveDevice(approval.id, code.replace(/\D/g, ""));
      toast.notify(`${approval.deviceName} freigegeben.`, { tone: "success" });
      props.onApproved();
    } catch (err: unknown) {
      setError(describeAuthError(err));
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="flex flex-col gap-2 py-3">
      <div className="text-sm">
        <p className="font-medium text-foam">{approval.deviceName}</p>
        <p className="text-xs text-mist-2">{approval.ip} · {formatRelativeTime(approval.createdAt, Date.now())}</p>
      </div>
      <form className="flex gap-2" onSubmit={(e): void => void submit(e)}>
        <TextInput
          aria-label={`Code von ${approval.deviceName}`}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="6-stelliger Code"
          value={code}
          onChange={(e): void => setCode(e.target.value)}
        />
        <Button type="submit" size="sm" loading={busy} disabled={code.replace(/\D/g, "").length !== 6}>Freigeben</Button>
      </form>
      {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
    </li>
  );
}

/**
 * Requests from new devices of this account (trusted-device policy). Only shown while one is
 * waiting; the new device displays the code to type here.
 */
export function DeviceApprovalsSection(): ReactNode {
  const queryClient = useQueryClient();
  const approvals = useQuery({
    queryKey: APPROVALS_KEY,
    queryFn: fetchDeviceApprovals,
    retry: false,
    refetchInterval: (query): number | false => (query.state.status === "error" ? false : 5000)
  });
  if (approvals.data === undefined || approvals.data.length === 0) {
    return null;
  }
  return (
    <Section
      id="device-approvals"
      title="Neue Geräte freigeben"
      description="Diese Geräte warten auf deine Freigabe. Gib den Code ein, den das neue Gerät anzeigt. Kennst du ein Gerät nicht, gib es nicht frei und ändere dein Master-Passwort."
    >
      <ul className="divide-y divide-line">
        {approvals.data.map((approval: PendingDeviceApproval): ReactNode => (
          <ApprovalRow
            key={approval.id}
            approval={approval}
            onApproved={(): void => void queryClient.invalidateQueries({ queryKey: APPROVALS_KEY })}
          />
        ))}
      </ul>
    </Section>
  );
}
